import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

/**
 * Global TooltipProvider — mounted once at the app root in main.tsx so
 * every <Tooltip> in the tree shares the same delay group. Re-exported
 * here so callers don't need to import the Radix package directly.
 */
export const TooltipProvider = TooltipPrimitive.Provider

interface TooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  side?: "top" | "right" | "bottom" | "left"
  /** Override delay for this tooltip only. Defaults to provider value (300ms). */
  delayDuration?: number
  /** Disable the tooltip without removing the wrapper — useful for conditional cases. */
  disabled?: boolean
}

/**
 * Compact wrapper around Radix tooltip primitives. Use it to label any
 * icon-only button so screen readers and hover users both get a name.
 *
 *   <Tooltip content="Edit Assessment">
 *     <Button variant="ghost" size="icon"><Pencil /></Button>
 *   </Tooltip>
 *
 * `asChild` on the Trigger means the Tooltip is "transparent" — the
 * child element receives the listeners, so click handlers on the button
 * still fire normally and there's no extra wrapper in the DOM.
 */
export function Tooltip({
  children,
  content,
  side = "top",
  delayDuration,
  disabled,
}: TooltipProps) {
  if (disabled || !content) return <>{children}</>
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={5}
          className="z-50 rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-md animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1"
        >
          {content}
          <TooltipPrimitive.Arrow className="fill-slate-900" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
